import { useCallback, useEffect, useState } from 'react'
import { api, ErroreApi } from '../servizi/api.ts'
import { Attesa, Avviso, Dato } from '../componenti/Pezzi.tsx'
import { AreaTesto, Bottone, Campo, Modale, Riquadro, Scelta, Spunta } from '../componenti/base.tsx'
import { AvvisoModulo, erroriDellApi, type Etichette } from '../componenti/errori-modulo.tsx'
import { FoglioModulo } from '../componenti/FoglioModulo.tsx'
import { indirizzoLogo } from '../servizi/stampa.ts'
import {
  NOMI_VERSIONI, type CampoModulo, type FoglioModulo as Foglio, type LogoDelProgetto, type ModelloModulo,
  type ModuloDelProgetto, type VersioneFami, type VoceModulo,
} from '../servizi/modulo-progetto.ts'

/**
 * «Modulo del destinatario» nella pagina del progetto (492; decisione di Davide del 31/10/2026: ogni
 * progetto ha il suo modulo). Chi legge il progetto vede il riepilogo e l'anteprima del modulo vuoto;
 * chi lo gestisce (`progetto.gestisci` nel perimetro) lo prepara partendo da una delle due versioni FAMI
 * 2021-2027 e corregge codice, beneficiario finale, titolo, titolare e allegati. Il modulo si stampa
 * compilato dalla scheda di ogni persona con un percorso nel progetto.
 */

const messaggio = (e: unknown) => (e instanceof ErroreApi ? e.message
  : 'Non è andata a buon fine: controlla la connessione e riprova.')

const momento = (valore: string) => new Date(valore).toLocaleString('it-IT', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
})

const ETICHETTE: Etichette = {
  sopratitolo: 'Riga sopra il titolo', fondo: 'Fondo o programma', titolo_scheda: 'Titolo del modulo',
  codice_identificativo: 'Codice identificativo di progetto', beneficiario_finale: 'Beneficiario finale',
  titolo_progetto: 'Titolo del progetto', loghi: 'Loghi', voci: 'Dati richiesti', informativa: 'Informativa',
  titolare: 'Titolare del trattamento', allegati: 'Allegati da chiedere',
}

/**
 * Riparte da una versione FAMI: righe, intestazione fissa, informativa e allegati della versione; restano
 * codice, beneficiario, titolo, titolare e loghi gia' scritti.
 */
function applicaVersione(bozza: ModelloModulo, versione: ModelloModulo): ModelloModulo {
  return {
    ...versione,
    codice_identificativo: bozza.codice_identificativo || versione.codice_identificativo,
    beneficiario_finale: bozza.beneficiario_finale || versione.beneficiario_finale,
    titolo_progetto: bozza.titolo_progetto || versione.titolo_progetto,
    titolare: bozza.titolare || versione.titolare,
    loghi: bozza.loghi.length ? bozza.loghi : versione.loghi,
  }
}

/** Il corpo da inviare: gli allegati si scrivono uno per riga. */
const corpo = (bozza: ModelloModulo, allegati: string): ModelloModulo => ({
  ...bozza, allegati: allegati.split('\n').map((a) => a.trim()).filter(Boolean),
})

export function ModuloDestinatario({ progettoId }: { progettoId: string }) {
  const [dati, setDati] = useState<ModuloDelProgetto | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [anteprima, setAnteprima] = useState(false)
  const [sceltaVersione, setSceltaVersione] = useState(false)
  const [bozza, setBozza] = useState<ModelloModulo | null>(null)
  const [esito, setEsito] = useState<string | null>(null)

  const carica = useCallback(() => {
    api.get<ModuloDelProgetto>(`/progetti/${progettoId}/modulo-destinatario`)
      .then((d) => { setDati(d); setErrore(null) })
      .catch((e) => setErrore(messaggio(e)))
  }, [progettoId])
  useEffect(carica, [carica])

  if (!dati) return errore ? <Avviso tipo="errore">{errore}</Avviso> : null
  const m = dati.modulo
  const obbligatori = m?.voci.filter((v) => (v.tipo === 'campo' || v.tipo === 'note') && v.obbligatorio).length ?? 0
  const facoltativi = m?.voci.filter((v) => (v.tipo === 'campo' || v.tipo === 'note') && !v.obbligatorio).length ?? 0

  return (
    <Riquadro titolo="Modulo del destinatario" azioni={dati.gestisce ? (
      m
        ? <Bottone variante="secondario" dimensione="piccolo" onClick={() => { setEsito(null); setBozza(m) }}>Modifica</Bottone>
        : <Bottone variante="secondario" dimensione="piccolo" onClick={() => setSceltaVersione(true)}>Parti dal modello FAMI 2021-2027</Bottone>
    ) : undefined}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {esito && <Avviso tipo="successo">{esito}</Avviso>}
      <p className="persona-nota-sezione">
        È la scheda anagrafica che la persona firma per il progetto, con l’intestazione del progetto, i dati richiesti e
        l’informativa del titolare del trattamento. Si stampa già compilata dalla scheda di ogni persona con un percorso
        nel progetto, si fa firmare e si carica fra i documenti della scheda.
      </p>
      {!m && (
        <p className="tenue">
          Il progetto non ha ancora un modulo del destinatario
          {dati.gestisce ? ': «Parti dal modello FAMI 2021-2027» lo prepara con i testi dei moduli FAMI.' : '.'}
        </p>
      )}
      {m && (
        <>
          {dati.loghi_tolti > 0 && (
            <Avviso tipo="attenzione">
              {dati.loghi_tolti === 1 ? 'Un logo scelto per il modulo non è più' : `${dati.loghi_tolti} loghi scelti per il modulo non sono più`}
              {' '}fra quelli del progetto: la stampa lo salta.
            </Avviso>
          )}
          <dl className="rete-dati">
            <Dato nome="Codice identificativo di progetto">{m.codice_identificativo}</Dato>
            <Dato nome="Beneficiario finale">{m.beneficiario_finale}</Dato>
            <Dato nome="Titolo del progetto">{m.titolo_progetto}</Dato>
            <Dato nome="Titolare del trattamento">{m.titolare}</Dato>
            <Dato nome="Dati richiesti">
              {`${obbligatori} ${obbligatori === 1 ? 'obbligatorio' : 'obbligatori'}, ${facoltativi} ${facoltativi === 1 ? 'facoltativo' : 'facoltativi'}`}
            </Dato>
            <Dato nome="Allegati da chiedere">{m.allegati.length ? m.allegati.join(', ') : null}</Dato>
            <Dato nome="Versione di partenza">{m.modello ? `FAMI 2021-2027, ${NOMI_VERSIONI[m.modello].nome.toLowerCase()}` : null}</Dato>
            {dati.aggiornato && (
              <Dato nome="Ultima modifica">{`${momento(dati.aggiornato.il)}${dati.aggiornato.da ? ` · ${dati.aggiornato.da}` : ''}`}</Dato>
            )}
          </dl>
          <Bottone variante="discreto" dimensione="piccolo" aria-expanded={anteprima} onClick={() => setAnteprima((a) => !a)}>
            {anteprima ? 'Nascondi l’anteprima' : 'Mostra l’anteprima del modulo vuoto'}
          </Bottone>
          {anteprima && dati.anteprima && (
            <FoglioModulo foglio={dati.anteprima} loghi={dati.loghi_anteprima} etichetta="Anteprima del modulo vuoto" />
          )}
        </>
      )}

      {sceltaVersione && dati.modelli && (
        <Modale titolo="Parti dal modello FAMI 2021-2027" larga chiudi={() => setSceltaVersione(false)}>
          <SceltaVersione modelli={dati.modelli} scegli={(v) => { setSceltaVersione(false); setEsito(null); setBozza(v) }} />
        </Modale>
      )}
      {bozza && dati.modelli && (
        <Modale titolo="Modulo del destinatario" larga chiudi={() => setBozza(null)}>
          <ModificaModulo progettoId={progettoId} dati={dati} iniziale={bozza} annulla={() => setBozza(null)}
                          salvato={(d) => { setBozza(null); setDati(d); setEsito('Modulo del destinatario salvato.') }} />
        </Modale>
      )}
    </Riquadro>
  )
}

/** Le due versioni FAMI, con quello che chiedono. */
function SceltaVersione({ modelli, scegli }: { modelli: Record<VersioneFami, ModelloModulo>; scegli: (m: ModelloModulo) => void }) {
  return (
    <>
      <p>
        Le due versioni riprendono i moduli FAMI mandati da CIDIS: intestazione «FONDO ASILO MIGRAZIONE E INTEGRAZIONE
        (2021-2027)», barra «SCHEDA ANAGRAFICA DEL DESTINATARIO», dati richiesti e informativa. Codice, beneficiario
        finale e titolo arrivano dal progetto; il titolare del trattamento va scritto.
      </p>
      <div className="modulo-versioni">
        {(Object.keys(NOMI_VERSIONI) as VersioneFami[]).map((v) => (
          <div className="modulo-versione" key={v}>
            <p><strong>{NOMI_VERSIONI[v].nome}</strong></p>
            <p className="piccolo">{NOMI_VERSIONI[v].descrizione}</p>
            <Bottone variante="primario" dimensione="piccolo" onClick={() => scegli(modelli[v])}>
              Usa la versione {NOMI_VERSIONI[v].nome.toLowerCase()}
            </Bottone>
          </div>
        ))}
      </div>
    </>
  )
}

/** Titolo di una riga del modulo nella modifica. */
function titoloVoce(v: VoceModulo, campi: Map<CampoModulo, string>): string {
  switch (v.tipo) {
    case 'campo': return `Dato della scheda: ${campi.get(v.campo) ?? v.campo}`
    case 'testo': return 'Riga di testo'
    case 'vulnerabilita': return 'Celle delle vulnerabilità (al massimo due, compilate dalla scheda)'
    case 'note': return 'Riga da compilare a mano'
  }
}

function ModificaModulo({ progettoId, dati, iniziale, salvato, annulla }: {
  progettoId: string
  dati: ModuloDelProgetto
  iniziale: ModelloModulo
  salvato: (d: ModuloDelProgetto) => void
  annulla: () => void
}) {
  const [bozza, setBozza] = useState<ModelloModulo>(iniziale)
  const [allegati, setAllegati] = useState(iniziale.allegati.join('\n'))
  const [daAggiungere, setDaAggiungere] = useState('')
  const [errore, setErrore] = useState<string | null>(null)
  const [errori, setErrori] = useState<Record<string, string>>({})
  const [inCorso, setInCorso] = useState<'salva' | 'anteprima' | null>(null)
  const [anteprima, setAnteprima] = useState<{ foglio: Foglio; loghi: LogoDelProgetto[] } | null>(null)

  const campi = new Map(dati.campi.map((c) => [c.codice, c.etichetta]))
  const cambia = (parte: Partial<ModelloModulo>) => { setBozza((b) => ({ ...b, ...parte })); setAnteprima(null) }
  const cambiaVoce = (i: number, voce: VoceModulo) => cambia({ voci: bozza.voci.map((v, j) => (j === i ? voce : v)) })
  const sposta = (i: number, verso: -1 | 1) => {
    const voci = [...bozza.voci]
    const [v] = voci.splice(i, 1)
    voci.splice(i + verso, 0, v)
    cambia({ voci })
  }
  const togli = (i: number) => cambia({ voci: bozza.voci.filter((_, j) => j !== i) })
  const aggiungi = (voce: VoceModulo) => cambia({ voci: [...bozza.voci, voce] })
  const usati = new Set(bozza.voci.flatMap((v) => (v.tipo === 'campo' ? [v.campo] : [])))
  const liberi = dati.campi.filter((c) => !usati.has(c.codice))
  const conVulnerabilita = bozza.voci.some((v) => v.tipo === 'vulnerabilita')
  const erroreVoci = Object.entries(errori).filter(([c]) => c === 'voci' || c.startsWith('voci.')).map(([, m]) => m).join(' ')

  async function invia(cosa: 'salva' | 'anteprima') {
    setInCorso(cosa); setErrore(null); setErrori({})
    try {
      if (cosa === 'salva') {
        salvato(await api.put<ModuloDelProgetto>(`/progetti/${progettoId}/modulo-destinatario`, corpo(bozza, allegati)))
      } else {
        setAnteprima(await api.post(`/progetti/${progettoId}/modulo-destinatario/anteprima`, corpo(bozza, allegati)))
      }
    } catch (e) {
      setErrore(messaggio(e))
      setErrori(erroriDellApi(e, { LOGHI_NON_AMMESSI: 'loghi' }))
    } finally { setInCorso(null) }
  }

  return (
    <form className="modulo-modifica" onSubmit={(e) => { e.preventDefault(); void invia('salva') }}>
      {dati.modelli && (
        <fieldset className="stampa-loghi-scelta">
          <legend>Parti dal modello FAMI 2021-2027</legend>
          <p className="campo-aiuto">
            Riprende righe, intestazione fissa, informativa e allegati della versione scelta. Codice, beneficiario finale,
            titolo, titolare e loghi già scritti restano.
          </p>
          <div className="modulo-voce-comandi">
            {(Object.keys(NOMI_VERSIONI) as VersioneFami[]).map((v) => (
              <Bottone key={v} variante="secondario" dimensione="piccolo" title={NOMI_VERSIONI[v].descrizione}
                       onClick={() => { const m = dati.modelli![v]; setAllegati(m.allegati.join('\n')); cambia(applicaVersione(bozza, m)) }}>
                Versione {NOMI_VERSIONI[v].nome.toLowerCase()}
              </Bottone>
            ))}
          </div>
        </fieldset>
      )}

      <h3 className="rete-titoletto">Intestazione</h3>
      <Campo etichetta="Riga sopra il titolo" valore={bozza.sopratitolo ?? ''} cambia={(v) => cambia({ sopratitolo: v })}
             maxLength={200} errore={errori.sopratitolo} aiuto="Facoltativa, per esempio «Scheda FAMI LUMEN»." />
      <Campo etichetta="Fondo o programma" valore={bozza.fondo ?? ''} cambia={(v) => cambia({ fondo: v })}
             maxLength={300} errore={errori.fondo} />
      <Campo etichetta="Titolo del modulo" valore={bozza.titolo_scheda} cambia={(v) => cambia({ titolo_scheda: v })}
             maxLength={200} obbligatorio errore={errori.titolo_scheda} aiuto="Sta nella barra sopra l’intestazione." />
      <Campo etichetta="Codice identificativo di progetto" valore={bozza.codice_identificativo}
             cambia={(v) => cambia({ codice_identificativo: v })} maxLength={80} obbligatorio
             errore={errori.codice_identificativo} aiuto="Il codice del programma, per esempio PROG-307." />
      <Campo etichetta="Beneficiario finale" valore={bozza.beneficiario_finale} cambia={(v) => cambia({ beneficiario_finale: v })}
             maxLength={300} obbligatorio errore={errori.beneficiario_finale} />
      <Campo etichetta="Titolo del progetto" valore={bozza.titolo_progetto} cambia={(v) => cambia({ titolo_progetto: v })}
             maxLength={300} obbligatorio errore={errori.titolo_progetto} />

      <fieldset className="stampa-loghi-scelta">
        <legend>Loghi nell’intestazione</legend>
        {dati.loghi_progetto.map((l) => (
          <Spunta key={l.id} valore={bozza.loghi.includes(l.id)}
                  cambia={(si) => cambia({
                    // Nell'ordine dei loghi del progetto.
                    loghi: dati.loghi_progetto.filter((x) => (x.id === l.id ? si : bozza.loghi.includes(x.id))).map((x) => x.id),
                  })}
                  etichetta={<span className="stampa-logo-voce"><img src={indirizzoLogo(l)} alt="" />{l.nome}</span>} />
        ))}
        {!dati.loghi_progetto.length && (
          <p className="campo-aiuto">
            Il progetto non ha loghi: si aggiungono nel riquadro «Loghi nelle stampe», per esempio Unione europea, FAMI e
            Ministero dell’Interno.
          </p>
        )}
        {errori.loghi && <p className="campo-errore" role="alert">{errori.loghi}</p>}
      </fieldset>

      <h3 className="rete-titoletto">Dati richiesti</h3>
      <p className="campo-aiuto">
        Nell’ordine del foglio. I dati obbligatori sono numerati, i facoltativi hanno l’asterisco e la nota «*dati
        facoltativi». La stampa li compila dalla scheda; quello che la scheda non ha resta da scrivere a mano. La riga
        «Note» resta sempre vuota: le note della scheda non si stampano.
      </p>
      {erroreVoci && <p className="campo-errore" role="alert">{erroreVoci}</p>}
      <ol className="modulo-voci">
        {bozza.voci.map((v, i) => (
          <li className="modulo-voce" key={i}>
            <div>
              <p className="modulo-voce-titolo">{titoloVoce(v, campi)}</p>
              {v.tipo === 'campo' && (
                <div className="modulo-voce-campi">
                  <Campo etichetta="Etichetta sul modulo" valore={v.etichetta ?? ''} maxLength={200}
                         segnaposto={campi.get(v.campo)} cambia={(t) => cambiaVoce(i, { ...v, etichetta: t || null })}
                         aiuto="Vuota: quella dei moduli FAMI." />
                  <Spunta etichetta="Obbligatorio" valore={v.obbligatorio} cambia={(o) => cambiaVoce(i, { ...v, obbligatorio: o })} />
                </div>
              )}
              {v.tipo === 'testo' && (
                <Campo etichetta="Testo" valore={v.testo} maxLength={500} cambia={(t) => cambiaVoce(i, { ...v, testo: t })}
                       aiuto="Per esempio «Per i cittadini di Paesi terzi indicare:»." />
              )}
              {v.tipo === 'vulnerabilita' && (
                <AreaTesto etichetta="Nota sopra le celle" valore={v.testo} righe={2} maxLength={500}
                           cambia={(t) => cambiaVoce(i, { ...v, testo: t })}
                           aiuto="Le celle si compilano con le vulnerabilità in corso della scheda, al massimo due; quelle riservate restano da scrivere a mano." />
              )}
              {v.tipo === 'note' && (
                <div className="modulo-voce-campi">
                  <Campo etichetta="Etichetta" valore={v.etichetta} maxLength={200} cambia={(t) => cambiaVoce(i, { ...v, etichetta: t })} />
                  <Spunta etichetta="Obbligatoria" valore={v.obbligatorio} cambia={(o) => cambiaVoce(i, { ...v, obbligatorio: o })} />
                </div>
              )}
            </div>
            <span className="modulo-voce-comandi">
              <Bottone variante="discreto" dimensione="piccolo" disabled={i === 0} onClick={() => sposta(i, -1)}>Su</Bottone>
              <Bottone variante="discreto" dimensione="piccolo" disabled={i === bozza.voci.length - 1} onClick={() => sposta(i, 1)}>Giù</Bottone>
              <Bottone variante="discreto" dimensione="piccolo" onClick={() => togli(i)}>Togli</Bottone>
            </span>
          </li>
        ))}
      </ol>
      <div className="modulo-aggiungi">
        {liberi.length > 0 && (
          <>
            <Scelta etichetta="Dato della scheda da aggiungere" valore={daAggiungere} cambia={setDaAggiungere} vuota="— scegli —"
                    opzioni={liberi.map((c) => ({ valore: c.codice, etichetta: c.etichetta }))} />
            <Bottone variante="secondario" dimensione="piccolo" disabled={!daAggiungere}
                     onClick={() => {
                       aggiungi({ tipo: 'campo', campo: daAggiungere as CampoModulo, obbligatorio: true, etichetta: null })
                       setDaAggiungere('')
                     }}>Aggiungi il dato</Bottone>
          </>
        )}
        <Bottone variante="secondario" dimensione="piccolo" onClick={() => aggiungi({ tipo: 'testo', testo: '' })}>
          Aggiungi una riga di testo
        </Bottone>
        {!conVulnerabilita && (
          <Bottone variante="secondario" dimensione="piccolo" onClick={() => aggiungi({
            tipo: 'vulnerabilita',
            testo: 'NB se si rilevano ulteriori condizioni di vulnerabilità è possibile indicarle nelle celle sottostanti (max 2)',
          })}>Aggiungi le celle delle vulnerabilità</Bottone>
        )}
        <Bottone variante="secondario" dimensione="piccolo" onClick={() => aggiungi({ tipo: 'note', etichetta: 'Note', obbligatorio: false })}>
          Aggiungi una riga «Note»
        </Bottone>
      </div>

      <h3 className="rete-titoletto">Informativa e titolare</h3>
      <Campo etichetta="Titolare del trattamento" valore={bozza.titolare} cambia={(v) => cambia({ titolare: v })} maxLength={300}
             obbligatorio errore={errori.titolare}
             aiuto="L’ente indicato dal progetto, per esempio «CIDIS IMPRESA SOCIALE ETS» o «CSV Lazio ETS»." />
      <AreaTesto etichetta="Testo dell’informativa" valore={bozza.informativa} righe={14} maxLength={20000}
                 cambia={(v) => cambia({ informativa: v })} obbligatorio errore={errori.informativa}
                 aiuto={`Si stampa su un foglio a parte, con data e firma. ${dati.segnaposti.map((s) => `${s.codice}: ${s.descrizione}`).join('; ')}. `
                   + 'Un dato che la scheda non ha resta a puntini.'} />
      <AreaTesto etichetta="Allegati da chiedere" valore={allegati} righe={3} cambia={(v) => { setAllegati(v); setAnteprima(null) }}
                 errore={errori.allegati} aiuto="Uno per riga. Sul foglio, in fondo: «ALLEGARE: …»." />

      <AvvisoModulo errore={errore} errori={errori} etichette={ETICHETTE} />
      <div className="azioni-modulo">
        <Bottone variante="discreto" onClick={annulla}>Annulla</Bottone>
        <Bottone variante="secondario" inCorso={inCorso === 'anteprima'} disabled={!!inCorso} onClick={() => void invia('anteprima')}>
          Anteprima
        </Bottone>
        <Bottone variante="primario" tipo="submit" inCorso={inCorso === 'salva'} disabled={!!inCorso}>Salva il modulo</Bottone>
      </div>
      {inCorso === 'anteprima' && <Attesa testo="Preparazione dell’anteprima…" />}
      {anteprima && <FoglioModulo foglio={anteprima.foglio} loghi={anteprima.loghi} etichetta="Anteprima del modulo vuoto" />}
    </form>
  )
}
